import type { FC } from "react";
import classnames from "classnames";
import useTranslation from "next-translate/useTranslation";

import { MatchPeriod } from "@motain/xpa-proto-files-web/lib/types/match_card";
import type { MatchScoreComponent } from "@motain/xpa-proto-files-web/lib/types/match_score";
import { TeamSide } from "@motain/xpa-proto-files-web/lib/types/team_side";

import { ImageWithFallback } from "@/components/image/ImageWithFallback";
import { isNonEmptyString } from "@/utils/type-guards/isNonEmptyStringGuard";
import { isNullable } from "@/types/isNullable";
import { isNonNullable } from "@/types/isNonNullable";
import { MatchScoreTeam } from "./match-score-team/MatchScoreTeam";
import { CountdownTimer } from "./countdown-timer/CountdownTimer";
import { LIVE_MATCH_PERIODS, POST_MATCH_PERIODS } from "./constants";
import styles from "./MatchScore.module.scss";
import { MatchScoreCompetition } from "./match-score-competition/MatchScoreCompetition";

const LIGHT_WARNING_ICON_URL =
  "https://images.onefootball.com/cw/icons/warning-light.svg";
const DARK_WARNING_ICON_URL =
  "https://images.onefootball.com/cw/icons/warning-dark.svg";

const MIN_IN_MS = 60 * 1000;
const HOUR_IN_MS = 60 * MIN_IN_MS;

type MatchScoreComponentProps = MatchScoreComponent & { isPreloaded?: boolean };

export const MatchScore: FC<MatchScoreComponentProps> = (props) => {
  const { t } = useTranslation("web-payments");

  const {
    homeTeam,
    awayTeam,
    kickoff,
    period,
    timePeriod,
    competition,
    isPreloaded = false,
  } = props;

  if (isNullable(homeTeam) || isNullable(awayTeam) || isNullable(kickoff)) {
    return <></>;
  }

  const isSecondLeg =
    isNonEmptyString(homeTeam.aggregatedScore) ||
    isNonEmptyString(awayTeam.aggregatedScore);

  const kickoffUTCdate = new Date(kickoff.utcTimestamp);
  const now = new Date();

  const isMatchInLessThanTwoHours =
    kickoffUTCdate.getTime() - now.getTime() < 2 * HOUR_IN_MS;
  const isMatchLive = LIVE_MATCH_PERIODS.includes(period);
  const isMatchOver = POST_MATCH_PERIODS.includes(period);
  const isMatchAbandonedWithNoScore =
    period === MatchPeriod.ABANDONED &&
    homeTeam.score === "" &&
    awayTeam.score === "";
  const isMatchAbandonedWithScore =
    period === MatchPeriod.ABANDONED &&
    isNonEmptyString(homeTeam.score) &&
    isNonEmptyString(awayTeam.score);

  const isPostPoned = period === MatchPeriod.POSTPONED;

  const isMatchWithScore =
    isMatchLive || isMatchAbandonedWithScore || isMatchOver;

  const isPreMatch = period === MatchPeriod.PRE_MATCH;

  return (
    <div className={classnames("enforced-dark", styles.patternContainer)}>
      <ImageWithFallback
        src={"https://images.onefootball.com/cw/bg/zigzag-pattern-banner.svg"}
        alt={""}
        isPreloaded={isPreloaded}
        fill
      ></ImageWithFallback>
      <div
        className={classnames(
          styles.container,
          isMatchWithScore && styles.wrapper,
        )}
      >
        <div
          className={classnames(
            styles.main,
            isMatchWithScore && styles.matchWithScore,
          )}
        >
          {isNonNullable(competition) && (
            <MatchScoreCompetition {...competition} />
          )}
          <MatchScoreTeam
            name={homeTeam.name}
            side={TeamSide.TEAM_SIDE_HOME}
            link={homeTeam.link}
            image={homeTeam.imageObject}
            trackingEvents={homeTeam.trackingEvents}
            isPreMatch={isPreMatch}
          />
          <div className={styles.data}>
            {isSecondLeg && (
              <p
                className={classnames("title-8-medium", styles.aggregatedScore)}
              >
                {period === MatchPeriod.PRE_MATCH
                  ? t("MATCH_FIRST_LEG")
                  : t("MATCH_AGGREGATE")}{" "}
                {homeTeam.aggregatedScore} - {awayTeam.aggregatedScore}
              </p>
            )}
            {period === MatchPeriod.PRE_MATCH && (
              <>
                <time
                  className={styles.kickoffDate}
                  dateTime={kickoff.utcTimestamp}
                >
                  <span
                    className={classnames("title-2-bold-druk", styles.numeric)}
                  >
                    {isMatchInLessThanTwoHours ? (
                      <CountdownTimer endsAt={kickoffUTCdate} />
                    ) : (
                      <>{kickoff.date}</>
                    )}
                  </span>
                </time>
                <time
                  className={styles.kickoffTime}
                  dateTime={kickoff.utcTimestamp}
                >
                  <span className={classnames("title-6-bold", styles.numeric)}>
                    {kickoff.time}
                  </span>
                </time>
              </>
            )}
            {(isPostPoned || isMatchAbandonedWithNoScore) && (
              <div className={styles.matchWithNoScore}>
                <ImageWithFallback
                  className={styles.warningIcon}
                  src={LIGHT_WARNING_ICON_URL}
                  darkModeSrc={DARK_WARNING_ICON_URL}
                  alt={t("WARNING_ICON_ALT_TEXT")}
                  width={22}
                  height={21}
                />
                <span className={styles.highlightedText}>{timePeriod}</span>
              </div>
            )}
            {isMatchLive && (
              <>
                <p className={classnames(styles.scores, "title-2-bold-druk")}>
                  <span>{homeTeam.score}</span>
                  <span>:</span>
                  <span>{awayTeam.score}</span>
                </p>
                <div className={styles.liveBadgeWrap}>
                  <span
                    className={classnames("title-8-medium", styles.liveBadge)}
                  >
                    {kickoff.liveBadge}
                  </span>
                  <span
                    className={classnames(
                      "title-8-medium",
                      styles.highlightedText,
                    )}
                  >
                    {timePeriod}
                  </span>
                </div>
              </>
            )}
            {(isMatchOver || isMatchAbandonedWithScore) && (
              <>
                <p className={classnames(styles.scores, "title-2-bold-druk")}>
                  <span>{homeTeam.score}</span>
                  <span>:</span>
                  <span>{awayTeam.score}</span>
                </p>
                <span
                  className={classnames(
                    "title-8-medium",
                    isMatchAbandonedWithScore && styles.highlightedText,
                  )}
                >
                  {timePeriod}
                </span>
              </>
            )}
          </div>
          <MatchScoreTeam
            name={awayTeam.name}
            side={TeamSide.TEAM_SIDE_AWAY}
            link={awayTeam.link}
            image={awayTeam.imageObject}
            trackingEvents={awayTeam.trackingEvents}
            isPreMatch={isPreMatch}
          />
        </div>
      </div>
    </div>
  );
};
